import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import useUploadFile from '@/hooks/files/use-upload-file';
import useUserStickers, { userStickersQueryOptions } from '@/hooks/stickers/use-user-stickers';
import { generateResponseScheme, type RespondedWith, type ResponseError } from '@/models/response';
import type { Uploadable } from '@/models/uploadable';
import { type UserSticker, userStickerScheme } from '@/models/user/custom-sticker';
import { addTranslateToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';
import { readableFileSize } from '@/utils/file-size';

// File validation constants
// utils/file-size.ts中thresh=1000
const STICKER_MAX_SIZE = 300 * 1000; // 300KB

export default function useAddSticker() {
  const { upload, isPending: isUploading } = useUploadFile();
  const queryClient = useQueryClient();
  const { isStickerLimitReached, stickerLimit } = useUserStickers();

  const mutation = useMutation<RespondedWith<UserSticker>, ResponseError, string | Uploadable>({
    mutationKey: ['/AddSticker'],
    mutationFn: async (sticker) => {
      const stickerUrl = typeof sticker === 'string' ? sticker : await upload(sticker);

      return await fetcher<RespondedWith<UserSticker>>(
        '/AddSticker',
        { StickerUrl: stickerUrl },
        {
          validResponseCodes: [0],
          schema: generateResponseScheme(userStickerScheme),
        },
      );
    },
    onSuccess: async (data) => {
      const newSticker = data.Data;

      if (!newSticker) {
        await queryClient.invalidateQueries(userStickersQueryOptions);
      } else {
        queryClient.setQueryData(userStickersQueryOptions.queryKey, (state) =>
          produce(state, (draft) => {
            if (!draft) return draft;

            draft.Stickers.push(newSticker);
          }),
        );
      }

      addTranslateToast({
        type: 'success',
        message: 'stickers.add-success',
      });
    },
    onError: () => {
      addTranslateToast({
        type: 'error',
        message: 'stickers.add-failed',
      });
    },
  });

  const addSticker = (sticker: string | Uploadable) => {
    if (isStickerLimitReached) {
      addTranslateToast({
        type: 'warning',
        message: `stickers.max-limit-reached|count=${stickerLimit}`,
      });
      throw new Error('STICKER_LIMIT_REACHED');
    }
    if (typeof sticker !== 'string') {
      if (!sticker.type.startsWith('image/')) {
        addTranslateToast({
          type: 'error',
          message: 'stickers.format-error',
        });
        throw new Error('INVALID_FILE_FORMAT');
      }
      if (sticker.size > STICKER_MAX_SIZE) {
        addTranslateToast({
          type: 'error',
          message: `errors.file-size-limit|limit=${readableFileSize(STICKER_MAX_SIZE, 0)}`,
        });
        throw new Error('FILE_TOO_LARGE');
      }
    }

    mutation.mutate(sticker);
  };

  return {
    ...mutation,
    addSticker,
    isPending: isUploading || mutation.isPending,
  };
}
